import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {ToastService} from "../../../services/Toast.service";
import {Session} from "../../../services/Session";
import {TpiqSelectDropdownComponent} from "../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {FormatAmountPipe} from "../../../pipes/format-amount.pipe";
import {ModelBasedNumeral} from "../../../directives/model-numeral";
import {LoadingService} from "../../../services/LoadingService";
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import {TpiqCustomDatepicker} from "../../../directives/tpiq-custom-datepicker";
import {
  calculateProposedFees,
  applyCarveOut,
  buildImplementationCalc,
  deriveBaseAmount,
  isCurbFeeLine,
  parseImplementationCalc,
  sumThirdPartyFees
} from "../../../tpiq-services/roi-fee-calculator.util";

declare const _: any;

@Component({
  selector: 'app-roi-fee-numbers',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqSelectDropdownComponent, FormatAmountPipe, ModelBasedNumeral, TpiqCustomDatepicker],
  templateUrl: './roi-fee-numbers.component.html',
  styleUrl: './roi-fee-numbers.component.scss'
})
export class RoiFeeNumbersComponent implements OnInit, OnChanges, OnDestroy {
  org: string = Session.getOrg();
  user: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  @Input() client: any;
  @Input() plan: any;
  @Input() year1Savings: any;
  @Input() year2Savings: any;
  /**
   * The plan's current cost lines, passed when an existing plan is opened in RAP.
   * The step then shows the fees the plan holds now. Without this it would show
   * the draft, which is a snapshot from the last RAP session.
   */
  @Input() planCostLines: any;
  finalY1ImplementationValue: any;
  calculatedY1ImplementationFee: any;
  finalY2ImplementationValue: any;

  implementationFeeTypes: Array<any> = [
    {type: 'Select All', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Complexity', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Urgency', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Risk', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Intangible Benefits', priority: 'medium', high: false, medium: true, low: false}
  ];
  implementationFeeColumns: Array<any> = [
    {name: 'type', title: ''},
    {name: 'high', title: 'High'},
    {name: 'medium', title: 'Medium'},
    {name: 'low', title: 'Low'}];
  feeLineData: any = {
    amount: 0,
    thirdPartyFee: 0
  };
  associatedWith: Array<any> = [
    {id: 'a627ccde-dd57-1090-cefa-fc965526a557', name: 'Effective Rate'},
    {id: '82c36191-8d6f-39e7-f711-947591551608', name: 'Marginal Rate'},
    {id: '5a314bc1-4534-0426-1db4-d18403485072', name: 'Cap Gain Rate'},
    {id: '23a5e1b2-03cf-6675-2596-0952387d4b46', name: 'C Corp Flat Rate'},
    {id: 'b870c7b6-802f-11eb-8aa0-1282986503dc', name: 'Non-deductible'}];
  feePeriods: Array<any> = [{name: 'One Time'}, {name: 'Monthly'}, {name: 'Yearly'}, {name: 'Custom'}];
  years: Array<any> = [];
  draftRoiExist = false;
  draftRoiFeeData: any;
  showPage = false;
  /** True when the user typed over the proposed fee. Persisted with the fee. */
  userOverride: boolean = false;
  /** True once this step has taken its values from the plan. */
  seeded: boolean = false;
  /** True once the user has changed something here. Seeding then stops. */
  userTouched: boolean = false;
  /** Baselines captured when the step opens. Third-party changes carve out of these. */
  curbBaseAmount: number | null = 0;
  hasStoredCurbState: boolean = false;

  constructor(
    private toastService: ToastService,
    private loadingService: LoadingService,
    private taxPlanningService: TaxPlanningService
  ) {}

  ngOnInit(): void {
    this.user = Session.getUser();
    if (this.seedFromPlanCostLines()) {
      this.fillYears();
      this.showPage = true;
      return;
    }
    this.getDraftSavedRoifee();
    this.fillYears();
  }

  /**
   * Seeds the step from the plan's current fees.
   *
   * Open in RAP must show what the plan holds now, the same way the create flow
   * shows what the user just entered. Returns false when the plan carries no
   * fees, and the draft path then runs as before.
   */
  seedFromPlanCostLines(): boolean {
    const lines = this.planCostLines;
    if (!lines || !lines.length) return false;
    const curbLine = _.find(lines, (line) => isCurbFeeLine(line));
    const prePaidLine = _.find(lines, {type: 'Pre Paid'});
    const thirdPartyTotal = sumThirdPartyFees(lines);
    if (!curbLine && !thirdPartyTotal && !prePaidLine) return false;

    const state = parseImplementationCalc(curbLine ? curbLine.implementationCalc : null);
    if (state.factors && state.factors.length) {
      this.implementationFeeTypes = state.factors;
    }
    this.feeLineData = {
      type: (curbLine && curbLine.type) || 'One Time',
      frequency: (curbLine && curbLine.frequency) || 'One Time',
      year: (curbLine && curbLine.year) || this.plan.taxYear,
      associatedWith: (curbLine && curbLine.associatedWith) || '82c36191-8d6f-39e7-f711-947591551608',
      description: (curbLine && curbLine.description) || '',
      amount: prePaidLine ? Number(prePaidLine.amount) || 0 : 0,
      thirdPartyFee: thirdPartyTotal
    };
    this.calculateImplementationFee();
    this.seeded = true;
    // Warning: calculateImplementationFee writes the override flag and the base
    // from a fresh calculation. The stored values must be applied after it.
    this.userOverride = state.userOverride;
    this.hasStoredCurbState = !!curbLine;
    this.curbBaseAmount = curbLine
      ? deriveBaseAmount(state, curbLine.amount, thirdPartyTotal)
      : this.curbBaseAmount;
    if (curbLine) {
      this.finalY1ImplementationValue = Number(curbLine.amount) || 0;
      this.calculatedY1ImplementationFee = state.systemAmount != null
        ? state.systemAmount
        : this.finalY1ImplementationValue;
    }
    return true;
  }

  getDraftSavedRoifee() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getDraftForPlan(this.org, this.user.id, this.plan.id)
      .subscribe({
        next: (response) => {
          this.loadingService.triggerLoadingEvent(false);
          this.draftRoiExist = true;
          this.draftRoiFeeData = response.roiFee;
          this.implementationFeeTypes = this.draftRoiFeeData.proposedY1ValueImplCalc || [
            {type: 'Select All', priority: 'medium', high: false, medium: true, low: false},
            {type: 'Complexity', priority: 'medium', high: false, medium: true, low: false},
            {type: 'Urgency', priority: 'medium', high: false, medium: true, low: false},
            {type: 'Risk', priority: 'medium', high: false, medium: true, low: false},
            {type: 'Intangible Benefits', priority: 'medium', high: false, medium: true, low: false}
          ];
          this.feeLineData = {
            type: 'One Time',
            frequency: 'Yearly',
            year: this.plan.taxYear,
            associatedWith: '82c36191-8d6f-39e7-f711-947591551608',
            amount: 0,
            thirdPartyFee: 0,
            ...(this.draftRoiFeeData.feeLineData || {})
          };
          this.feeLineData.amount = Number(this.feeLineData.amount) || 0;
          this.feeLineData.thirdPartyFee = Number(this.feeLineData.thirdPartyFee) || 0;
          this.finalY1ImplementationValue = this.draftRoiFeeData.proposedY1Value || 0;
          this.calculateImplementationFee();
          this.showPage = true;
        },
        error: (error) => {
          this.loadingService.triggerLoadingEvent(false);
          this.draftRoiExist = false;
          this.feeLineData = {
            type: 'One Time',
            frequency: 'One Time',
            year: this.plan.taxYear,
            associatedWith: '82c36191-8d6f-39e7-f711-947591551608',
            amount: 0,
            thirdPartyFee: 0
          };
          this.calculateImplementationFee();
          this.showPage = true;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The fees can arrive after this step has rendered. Seed then, but never
    // over the top of edits the user has already made in this session.
    if (changes['planCostLines'] && changes['planCostLines'].currentValue && !this.seeded && !this.userTouched) {
      if (this.seedFromPlanCostLines()) this.showPage = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  setImplementationFeePriority(row, column) {
    row.priority = column.name;
    row[column.name] = true;
    if (column.name === 'high') {
      row.medium = false;
      row.low = false;
    } else if (column.name === 'medium') {
      row.high = false;
      row.low = false;
    } else if (column.name === 'low') {
      row.high = false;
      row.medium = false;
    }

    if (row.type === 'Select All') {
      _.each(this.implementationFeeTypes, (line) => {
        if (row.priority === 'high') {
          line.high = true;
          line.medium = false;
          line.low = false;
        } else if (row.priority === 'medium') {
          line.medium = true;
          line.high = false;
          line.low = false;
        } else if (row.priority === 'low') {
          line.low = true;
          line.high = false;
          line.medium = false;
        }
        line.priority = row.priority;
      });
    } else {
      _.each(this.implementationFeeTypes, (line) => {
        if (line.type === 'Select All') {
          line.priority = '';
          line.high = false;
          line.medium = false;
          line.low = false;
        }
      });
    }
    this.userTouched = true;
    this.calculateImplementationFee();
  }

  setAssociatedWithForFeeItem($event) {
    this.feeLineData.associatedWith = $event.id;
  }

  setPeriod($event){
     this.feeLineData.fromMonth = null;
     this.feeLineData.toMonth = null; 
    this.feeLineData.frequency = $event.name;
  }

  setYear($event){
    this.feeLineData.year = $event.id;
  }

  fillYears(){
    const startYear: any = _.toNumber(this.plan.taxYear);
    const endYear: any = startYear + 10;
    for (let i = startYear; i < endYear; i++){
      this.years.push({id: i, name: i.toString()});
    }
  }

onDate($event: Date | String, type?: string) {
  if (type === 'start') {
    this.feeLineData.fromMonth = $event;
  } else if (type === 'end') {
    this.feeLineData.toMonth = $event;
  }
}

  setTypeOfFee(type) {
    this.feeLineData.type = type;
    if (type === 'One Time') {
      this.feeLineData.frequency = 'One Time';
    } else if (type === 'Recurring') {
      this.feeLineData.frequency = 'Yearly';
    }
  }

  calculateImplementationFee() {
    if (this.validateImplementationFeeLines()) {
      const {y1Proposed, y2Proposed, y1Base} = calculateProposedFees({
        year1Savings: this.year1Savings,
        year2Savings: this.year2Savings,
        implementationFeeTypes: this.implementationFeeTypes,
        thirdPartyFee: this.feeLineData?.thirdPartyFee
      });
      this.finalY1ImplementationValue = y1Proposed;
      this.calculatedY1ImplementationFee = y1Proposed;
      this.finalY2ImplementationValue = y2Proposed;
      this.curbBaseAmount = y1Base;
      // A CURB factor change is a fresh system calculation, so it clears the override.
      this.userOverride = false;
    } else {
      this.toastService.pop(TOAST_TYPE.error, 'Please select priority for any one type.');
    }
  }

  validateImplementationFeeLines() {
    let existValue = false;
    _.each(this.implementationFeeTypes, (line) => {
      if (line.priority) {
        existValue = true;
      }
    });
    return existValue;
  }

  roundOffTheValue(value) {
    if (value) {
      value = _.round(value);
    }
  }

  /**
   * Returns the current ROI/Fee data for RAP Edit sandbox sync
   * This method is called by the dashboard-upload-create-plan component
   * during the RAP "Open in RAP" flow to sync fee data before commit.
   */
  getRapEditData() {
    return {
      feeLineData: this.feeLineData || {},
      proposedY1Value: this.finalY1ImplementationValue || 0,
      // The fee state rides in the leading meta element, so it survives the
      // commit endpoint. That field is a List<Object> and must stay an array.
      proposedY1ValueImplCalc: buildImplementationCalc({
        factors: this.implementationFeeTypes || [],
        userOverride: this.userOverride === true,
        systemAmount: Number(this.calculatedY1ImplementationFee) || 0,
        baseAmount: this.curbBaseAmount != null ? Number(this.curbBaseAmount) || 0 : null
      })
    };
  }

  /**
   * Restores the system calculated fee.
   *
   * Warning: this must not call calculateImplementationFee when the step was
   * seeded from the plan. That reads year1Savings, which can be 0 here.
   */
  resetProposedFee() {
    this.finalY1ImplementationValue = this.calculatedY1ImplementationFee;
    this.userOverride = false;
  }

  /** Marks the proposed fee as user set. */
  onProposedFeeBlur() {
    this.userTouched = true;
    const typed = Number(this.finalY1ImplementationValue) || 0;
    const calculated = Number(this.calculatedY1ImplementationFee) || 0;
    this.userOverride = typed !== calculated;
  }

  onThirdPartyFeeBlur() {
    this.userTouched = true;
    this.feeLineData.thirdPartyFee = _.round(Number(this.feeLineData.thirdPartyFee) || 0);
    if (this.hasStoredCurbState) {
      if (this.curbBaseAmount == null) return;
      const moved = applyCarveOut(this.curbBaseAmount, this.feeLineData.thirdPartyFee);
      this.calculatedY1ImplementationFee = moved;
      if (!this.userOverride) this.finalY1ImplementationValue = moved;
      return;
    }
    this.calculateImplementationFee();
  }

}
